export interface ValueLabelBox {
  x: number;
  y: number;
  width: number;
  height: number;
  barX: number;
  barY: number;
  barWidth: number;
  barHeight: number;
  negative?: boolean;
  kind: 'current' | 'predicted';
  visible: boolean;
  inside: boolean;
}
export interface PlotBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export const labelsOverlap = (a: ValueLabelBox, b: ValueLabelBox, gap = 2) =>
  a.visible &&
  b.visible &&
  Math.min(a.x + a.width / 2, b.x + b.width / 2) + gap > Math.max(a.x - a.width / 2, b.x - b.width / 2) &&
  Math.min(a.y + a.height / 2, b.y + b.height / 2) + gap > Math.max(a.y - a.height / 2, b.y - b.height / 2);

const fitsInside = (p: ValueLabelBox) => p.width + 4 <= Math.abs(p.barWidth) && p.height + 4 <= Math.abs(p.barHeight);
const barEnd = (p: ValueLabelBox) =>
  p.negative ? Math.max(p.barY, p.barY + p.barHeight) : Math.min(p.barY, p.barY + p.barHeight);

/** Use measured text sizes, place within the plot, then resolve all pairs including neighbouring slots. */
export interface ReferenceMark {
  left: number;
  right: number;
  y: number;
}

export function layoutValueLabels(
  input: ValueLabelBox[],
  bounds: PlotBounds,
  marks: ReferenceMark[] = [],
): ValueLabelBox[] {
  const labels = input.map((p) => {
    const negative = p.negative ?? p.barHeight < 0;
    const inside = fitsInside(p);
    const x = Math.max(bounds.left + p.width / 2, Math.min(p.barX + p.barWidth / 2, bounds.right - p.width / 2));
    const end = barEnd({ ...p, negative });
    const y = inside
      ? p.barY + p.barHeight / 2
      : negative
        ? end + p.height / 2 + 3
        : Math.max(bounds.top + p.height / 2, end - p.height / 2 - 3);
    return {
      ...p,
      negative,
      inside,
      x,
      y,
      visible:
        p.width <= bounds.right - bounds.left && y - p.height / 2 >= bounds.top && y + p.height / 2 <= bounds.bottom,
    };
  });
  for (let attempts = 0; attempts < labels.length * labels.length * 2; attempts++) {
    let conflict: [ValueLabelBox, ValueLabelBox] | undefined;
    for (let i = 0; i < labels.length && !conflict; i++)
      for (let j = i + 1; j < labels.length; j++) {
        if (labelsOverlap(labels[i], labels[j])) {
          conflict = [labels[i], labels[j]];
          break;
        }
      }
    if (!conflict) break;
    const [a, b] = conflict;
    const higher = barEnd(a) < barEnd(b) ? a : b;
    const lower = higher === a ? b : a;
    const raised = Math.min(higher.y, lower.y) - lower.height / 2 - higher.height / 2 - 2;
    if (
      raised - higher.height / 2 >= bounds.top &&
      (!higher.negative || raised - higher.height / 2 >= barEnd(higher) + 3)
    ) {
      higher.y = raised;
      higher.inside = false;
    } else {
      const booked = a.kind === 'current' ? a : b.kind === 'current' ? b : undefined;
      const inset = booked && { ...booked, y: booked.barY + booked.barHeight / 2, inside: true };
      if (
        inset &&
        fitsInside(inset) &&
        inset.y - inset.height / 2 >= bounds.top &&
        inset.y + inset.height / 2 <= bounds.bottom &&
        !labels.some((label) => label !== booked && labelsOverlap(inset, label))
      ) {
        Object.assign(booked, inset);
      } else {
        const smaller = Math.abs(a.barHeight) <= Math.abs(b.barHeight) ? a : b;
        smaller.visible = false;
      }
    }
  }
  // Resolve reference marks after label pairs, checking all other labels before moving.
  for (const label of labels.filter((p) => p.visible)) {
    const hits = (candidate: ValueLabelBox) =>
      marks.filter(
        (mark) =>
          candidate.x + candidate.width / 2 + 2 > mark.left &&
          candidate.x - candidate.width / 2 - 2 < mark.right &&
          Math.abs(candidate.y - mark.y) < candidate.height / 2 + 4,
      );
    for (let attempt = 0; attempt <= marks.length && hits(label).length; attempt++) {
      const y = Math.min(...hits(label).map((mark) => mark.y)) - label.height / 2 - 5;
      const raised = { ...label, y, inside: false };
      while (hits(raised).length) raised.y = Math.min(...hits(raised).map((mark) => mark.y)) - raised.height / 2 - 5;
      const inset = { ...label, y: label.barY + label.barHeight / 2, inside: true };
      const fits = (candidate: ValueLabelBox) =>
        candidate.y - candidate.height / 2 >= bounds.top &&
        candidate.y + candidate.height / 2 <= bounds.bottom &&
        !hits(candidate).length &&
        !labels.some((other) => other !== label && labelsOverlap(candidate, other));
      if (fits(raised)) Object.assign(label, raised);
      else if (fitsInside(inset) && fits(inset)) Object.assign(label, inset);
      else label.visible = false;
      if (!label.visible) break;
    }
  }
  return labels;
}
